import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { refreshAuthMeContext } from "@/features/auth/services/auth-query"

import {
  type ChurchRegistrationRequest,
  fetchOnboardingDefaults,
  registerChurch,
} from "./create-church-api"

export const ONBOARDING_DEFAULTS_QUERY_KEY = ["create-church", "defaults"] as const

/** Server geo suggestion for the wizard's smart defaults. Never blocks the wizard — failure is ignored. */
export function useOnboardingDefaultsQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ONBOARDING_DEFAULTS_QUERY_KEY,
    queryFn: fetchOnboardingDefaults,
    enabled: options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

/**
 * Register a church as the signed-in user.
 *
 * Refetches the auth context on success rather than patching it: the new workspace changes what the caller
 * can reach (nav, guards, branch resolution) and the server is the only thing that knows the whole shape of
 * that. The endpoint also repoints their default workspace, so a stale `authMe` would send them back to the
 * church they came from.
 */
export function useRegisterChurchMutation() {
  const queryClient = useQueryClient()
  return useMutation(registerChurchMutationOptions(queryClient))
}

export function registerChurchMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: (input: ChurchRegistrationRequest) => registerChurch(input),
    // Awaited (returned) so `mutateAsync`, and the navigation after it, only resolve once the authoritative auth
    // store holds the new workspace. Invalidating `["auth","me"]` alone left guards reading the previous
    // church's access: the new church opened as "no module access" until a hard reload.
    onSuccess: () => refreshAuthMeContext(queryClient),
  }
}

